import React, { useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { openDialog } from '@/features/dialog/dialogSlice';
import { AdminDataGrid } from '../../shared/AdminDataGrid';
import { useAdminListParams } from '../../shared/useAdminListParams';
import { useListFormFieldConfigsQuery } from '../api/formFieldConfigsApi';
import {
	SHARE_FORM_FIELD_CONFIGS_DIALOG,
	ShareFormFieldConfigsDialog,
} from '../ShareFormFieldConfigsDialog';
import { useFormFieldConfigScope } from '../useFormFieldConfigScope';
import { CONFIG_TYPE, isScopeChosen } from '../scopes';
import { formFieldConfigColumns } from './columns';

// Text settings edited in their cells
const TEXT_FIELDS = ['label', 'helperText', 'description'];

/**
 * The form field configs the page's scope sees, one per field, showing the form's unsaved
 * values. Text cells and switches change the Formik values; the footer saves them. Ticked rows
 * can be shared (ShareFormFieldConfigsDialog) once there are no unsaved changes.
 */
export const FormFieldConfigsTable = () => {
	const dispatch = useDispatch();
	const { scope } = useFormFieldConfigScope();
	const { values, dirty, setFieldValue } = useFormikContext();
	const { gridProps } = useAdminListParams('formFieldConfigs');
	// currentData, not data: data keeps the previous scope's rows while a new scope loads
	const {
		currentData: configs,
		isFetching,
		isError,
	} = useListFormFieldConfigsQuery(
		{ ...scope, configType: CONFIG_TYPE },
		// Empty until a ledger or broker group is picked
		{ skip: !isScopeChosen(scope) },
	);
	const [selectedIds, setSelectedIds] = useState([]);
	const isShareOpen = useSelector((state) => state.dialog.open[SHARE_FORM_FIELD_CONFIGS_DIALOG]);

	const rows = useMemo(
		() => (configs ?? []).map((config) => ({ ...config, ...values[config.id] })),
		[configs, values],
	);

	// Keeps edited text in the form
	const handleRowUpdate = (newRow, oldRow) => {
		TEXT_FIELDS.filter((field) => newRow[field] !== oldRow[field]).forEach((field) =>
			setFieldValue(`${oldRow.id}.${field}`, newRow[field]),
		);
		return newRow;
	};

	return (
		<>
			<AdminDataGrid
				table='formFieldConfigs'
				columns={formFieldConfigColumns}
				rows={rows}
				isLoading={isFetching}
				isError={isError}
				objectType='form field configs'
				minHeight='75vh'
				processRowUpdate={handleRowUpdate}
				checkboxSelection
				disableRowSelectionOnClick
				rowSelectionModel={selectedIds}
				onRowSelectionModelChange={setSelectedIds}
				toolbarActions={
					<Button
						variant='table_actions'
						disabled={!selectedIds.length || dirty}
						onClick={() =>
							dispatch(
								openDialog({
									dialogName: SHARE_FORM_FIELD_CONFIGS_DIALOG,
									data: { docIds: selectedIds },
								}),
							)
						}
						sx={{ ml: 'auto' }}
					>
						{selectedIds.length ? `Share (${selectedIds.length})` : 'Share'}
					</Button>
				}
				{...gridProps}
			/>
			{/* Outside the grid, so typing in its pickers isn't caught by the grid's keyboard handling */}
			{isShareOpen && <ShareFormFieldConfigsDialog />}
		</>
	);
};

FormFieldConfigsTable.displayName = 'FormFieldConfigsTable';
